import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import LandingPage from '@/components/landing/LandingPage';
import Dashboard from './Dashboard';
import AuthModal from '@/components/auth/AuthModal';
import LoadingScreen from '@/components/auth/LoadingScreen';
// Phone/PIN verification temporarily disabled — see note below.

const Index = () => {
  const { user, session, loading, signOut } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Handle openAuth navigation state (e.g. from /auth/confirmed redirect)
  useEffect(() => {
    if ((location.state as any)?.openAuth) {
      setShowAuthModal(true);
      // Clean state so it doesn't re-trigger
      window.history.replaceState({}, '', '/');
    }
  }, [location.state]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Fetch user profile when user is authenticated
  useEffect(() => {
    if (user && session) {
      fetchUserProfile();
    }
  }, [user, session]);

  const fetchUserProfile = async () => {
    if (!user) return;
    
    setProfileLoading(true);
    try {
      const { data: profile, error } = await (supabase
        .from('profiles')
        .select('display_name, is_onboarded, phone_number')
        .eq('id', user.id)
        .single() as any);

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        return;
      }

      setUserProfile(profile);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // Profile will be fetched by useEffect when user changes
  };

  const handleLogout = async () => {
    await signOut();
    setUserProfile(null);
  };

  // Show loading state while checking authentication
  if (loading || profileLoading) {
    return <LoadingScreen message="Loading your account..." />;
  }

  // User is authenticated - go directly to dashboard
  // NOTE: Phone verification and PIN flows are temporarily disabled while we
  // iterate on the broader internal experience. Re-enable once the OTP/PIN
  // pipeline is hardened.
  if (user && session) {
    return <Dashboard onLogout={handleLogout} />;
  }

  // User is not authenticated - show landing page
  return (
    <>
      <LandingPage onAuthClick={() => setShowAuthModal(true)} />
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </>
  );
};

export default Index;
